import React, { useEffect, useState, useCallback } from "react";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import { useWorkspace } from "../../contexts/Workspacecontext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./Connections.scss";
import RecruitmentAgreementModal, {
  generatePDFBlob,
  submitAgreement,
} from "./Recruitmentagreement";
import LockedButton from "../../components/LockedButton";
import { RECRUITMENT } from "../../config/moduleAccess";

const HR_BASE = `${process.env.REACT_APP_API_HR_URL}/connections`;
const CADMIN = `${process.env.REACT_APP_API_CONSULTANT_ADMIN_URL}/connections`;

/* ─────────────────── HELPERS ─────────────────── */
const SEED_COLORS = [
  "#2563eb",
  "#0f6e56",
  "#d97706",
  "#7c3aed",
  "#dc2626",
  "#0891b2",
  "#db2777",
  "#15803d",
];
const seedColor = (s = "") =>
  SEED_COLORS[(String(s).charCodeAt(0) || 0) % SEED_COLORS.length];
const initials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";
const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
const normVal = (v = "") => (v ? v.toUpperCase().replace(/[\s-]/g, "_") : "");
const normDeep = (val) => {
  if (Array.isArray(val)) return val.map(normDeep);
  if (val && typeof val === "object")
    return Object.fromEntries(
      Object.entries(val).map(([k, v]) => [
        k,
        /status/i.test(k) && typeof v === "string" ? normVal(v) : normDeep(v),
      ]),
    );
  return val;
};
const normModule = (v = "") =>
  v ? v.toUpperCase().replace(/[\s-]/g, "_") : "";
const isBlank = (h) => !h || h === "<p><br></p>" || h.trim() === "";
const normConsultancy = (item) => ({
  ...item,
  connectionCompanyName: item.connectionCompanyName ?? item.companyName,
  moduleAggrementStatus: normVal(
    item.moduleAggrementStatus ??
      item.moduleAgreementStatus ??
      item.moduleAgreemntStatus,
  ),
  senderDocumentStatus: normVal(item.senderDocumentStatus),
  receiverDocumentStatus: normVal(item.receiverDocumentStatus),
});

/* ─────────────────── STATUS CONFIG ─────────────────── */
const STATUS_MAP = {
  INVITED: { label: "Invite Sent", color: "#2563eb", bg: "#eff6ff" },
  ACCEPTED: { label: "Connected", color: "#15803d", bg: "#dcfce7" },
  REJECTED: { label: "Rejected", color: "#dc2626", bg: "#fee2e2" },
  REQUESTED: { label: "Pending", color: "#d97706", bg: "#fef3c7" },
  REQUEST_ACCEPTED: { label: "Connected", color: "#15803d", bg: "#dcfce7" },
  REQUEST_REJECTED: { label: "Rejected", color: "#dc2626", bg: "#fee2e2" },
  DRAFT: { label: "Draft", color: "#6b7280", bg: "#f3f4f6" },
  SENT: { label: "T&C Sent", color: "#2563eb", bg: "#eff6ff" },
  SENT_ACCEPTED: { label: "T&C Accepted", color: "#15803d", bg: "#dcfce7" },
  SENT_REJECTED: { label: "T&C Rejected", color: "#dc2626", bg: "#fee2e2" },
  RESENT: { label: "T&C Resent", color: "#d97706", bg: "#fef3c7" },
  RECEIVED: { label: "Received", color: "#2563eb", bg: "#eff6ff" },
  RECEIVED_ACCEPTED: { label: "You Accepted", color: "#15803d", bg: "#dcfce7" },
  RECEIVED_REJECTED: { label: "You Rejected", color: "#dc2626", bg: "#fee2e2" },
  RE_RECEIVED: { label: "Updated", color: "#d97706", bg: "#fef3c7" },
};
const getStatus = (s) =>
  STATUS_MAP[s] || { label: s || "—", color: "#6b7280", bg: "#f3f4f6" };

const QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "blockquote"],
      ["clean"],
    ],
  },
  clipboard: { matchVisual: false },
};
const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "list",
  "bullet",
  "indent",
  "link",
  "blockquote",
];

/* ═══════════════════════════════════════════
   ATOMS
═══════════════════════════════════════════ */
const StatusPill = ({ status }) => {
  const s = getStatus(status);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: s.bg,
        color: s.color,
        borderRadius: 10,
        padding: "3px 10px",
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: s.color,
        }}
      />
      {s.label}
    </span>
  );
};

const Avatar = ({ name, logo, logoType, sz = 40 }) => {
  if (logo)
    return (
      <img
        style={{
          width: sz,
          height: sz,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
        }}
        src={`data:${logoType || "image/png"};base64,${logo}`}
        alt={name}
      />
    );
  return (
    <div
      style={{
        width: sz,
        height: sz,
        borderRadius: "50%",
        background: seedColor(name),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: sz * 0.3,
        fontWeight: 700,
        color: "#fff",
        flexShrink: 0,
        letterSpacing: "-0.5px",
      }}
    >
      {initials(name)}
    </div>
  );
};

const Spin = ({ dark }) => (
  <div
    style={{
      width: 16,
      height: 16,
      borderRadius: "50%",
      border: `2px solid ${dark ? "#d1d5db" : "rgba(255,255,255,0.35)"}`,
      borderTopColor: dark ? "#374151" : "#fff",
      animation: "cn-spin 0.65s linear infinite",
      display: "inline-block",
    }}
  />
);

const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span
        style={{
          width: 15,
          height: 15,
          borderRadius: "50%",
          background: "#e5e7eb",
          color: "#6b7280",
          fontSize: 10,
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "help",
        }}
      >
        ?
      </span>
      {show && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1f2937",
            color: "#f9fafb",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 12,
            lineHeight: 1.5,
            width: 200,
            zIndex: 1000,
            whiteSpace: "normal",
            pointerEvents: "none",
            boxShadow: "0 4px 20px rgba(0,0,0,.3)",
          }}
        >
          {text}
        </div>
      )}
    </span>
  );
};

const SectionHeader = ({ icon, title, count, accent = "#2563eb", info }) => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        background: accent + "18",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
          {title}
        </span>
        {count !== undefined && (
          <span
            style={{
              background: accent + "18",
              color: accent,
              borderRadius: 12,
              padding: "1px 8px",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {count}
          </span>
        )}
        {info && <InfoTooltip text={info} />}
      </div>
    </div>
  </div>
);

const StatCard = ({ label, value, sub, accent }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: "14px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 0,
      position: "relative",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
      }}
    >
      <div>
        <p
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#6b7280",
            margin: 0,
            marginBottom: 2,
          }}
        >
          {label}
        </p>
        {sub && (
          <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>{sub}</p>
        )}
      </div>
      <p
        style={{
          fontSize: 26,
          fontWeight: 800,
          color: accent,
          margin: 0,
          lineHeight: 1,
        }}
      >
        {value}
      </p>
    </div>
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: 3,
        background: accent,
        opacity: 0.25,
      }}
    />
  </div>
);

const EmptyCard = ({ icon = "○", heading, note }) => (
  <div
    style={{
      textAlign: "center",
      padding: "10px",
      borderRadius: 12,
      border: "1.5px dashed #e5e7eb",
      background: "#fafafa",
    }}
  >
    <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
    <div
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: "#374151",
        marginBottom: 4,
      }}
    >
      {heading}
    </div>
    {note && <div style={{ fontSize: 12, color: "#9ca3af" }}>{note}</div>}
  </div>
);

const Loading = ({ text = "Loading…" }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 0",
      justifyContent: "center",
      color: "#6b7280",
      fontSize: 13,
    }}
  >
    <Spin dark /> {text}
  </div>
);

const Btn = ({ v = "primary", sm, disabled, onClick, loading, children }) => {
  const STYLES = {
    primary: { bg: "#2563eb", color: "#fff", border: "#2563eb" },
    success: { bg: "#15803d", color: "#fff", border: "#15803d" },
    danger: { bg: "#dc2626", color: "#fff", border: "#dc2626" },
    ghost: { bg: "transparent", color: "#374151", border: "#d1d5db" },
    outline: { bg: "#eff6ff", color: "#2563eb", border: "#2563eb" },
    amber: { bg: "#fef3c7", color: "#d97706", border: "#fbbf24" },
    green: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    red: { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5" },
    neutral: { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" },
    tricolor: {
      bg: "linear-gradient(135deg,#ff9933 0%,#fff 50%,#138808 100%)",
      color: "#000080",
      border: "#ff9933",
    },
  };
  const s = STYLES[v] || STYLES.primary;
  return (
    <button
      disabled={disabled || loading}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: sm ? "5px 12px" : "8px 16px",
        borderRadius: 20,
        fontSize: sm ? 12 : 13,
        fontWeight: 600,
        border: `1.5px solid ${s.border}`,
        background: s.bg,
        color: s.color,
        cursor: disabled || loading ? "not-allowed" : "pointer",
        opacity: disabled && !loading ? 0.5 : 1,
        transition: "opacity .15s, transform .1s",
        fontFamily: "inherit",
        whiteSpace: "nowrap",
      }}
      onMouseDown={(e) => {
        if (!disabled) e.currentTarget.style.transform = "scale(0.97)";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "";
      }}
    >
      {loading && <Spin />}
      {children}
    </button>
  );
};

const Modal = ({ onClose, width = 560, children }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,.45)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
    }}
    onMouseDown={onClose}
  >
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        width: "100%",
        maxWidth: width,
        maxHeight: "90vh",
        overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,.25)",
        animation: "cn-modal-in .2s ease",
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
);

const ModalHead = ({ title, sub, onClose }) => (
  <div
    style={{
      padding: "10px",
      borderBottom: "1px solid #f3f4f6",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
    }}
  >
    <div>
      <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>
        {title}
      </div>
      {sub && (
        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
    <button
      onClick={onClose}
      style={{
        width: 30,
        height: 30,
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        background: "#f9fafb",
        cursor: "pointer",
        fontSize: 14,
        color: "#6b7280",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      ✕
    </button>
  </div>
);

const ModalFoot = ({ children }) => (
  <div
    style={{
      padding: "10px",
      borderTop: "1px solid #f3f4f6",
      display: "flex",
      justifyContent: "flex-end",
      gap: 10,
      background: "#fafafa",
      borderRadius: "0 0 16px 16px",
    }}
  >
    {children}
  </div>
);

const TCViewer = ({ html, label }) => {
  if (!html || isBlank(html)) return null;
  return (
    <div
      style={{
        margin: "10px",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: "#f9fafb",
          padding: "8px 14px",
          fontSize: 11,
          fontWeight: 700,
          color: "#6b7280",
          letterSpacing: "0.05em",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        {label || "Terms & Conditions"}
      </div>
      <div
        style={{
          padding: "14px 16px",
          fontSize: 13,
          color: "#374151",
          lineHeight: 1.7,
          maxHeight: 200,
          overflowY: "auto",
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

const QuillField = ({
  label,
  value,
  onChange,
  placeholder,
  error,
  required,
}) => (
  <div style={{ padding: "10px" }}>
    <label
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: "#374151",
        display: "block",
        marginBottom: 6,
      }}
    >
      {label}
      {required && <span style={{ color: "#dc2626", marginLeft: 2 }}>*</span>}
    </label>
    <ReactQuill
      value={value}
      onChange={onChange}
      modules={QUILL_MODULES}
      formats={QUILL_FORMATS}
      placeholder={placeholder}
      theme="snow"
      style={{
        border: error ? "1px solid #dc2626" : undefined,
        borderRadius: 8,
      }}
    />
    {error && (
      <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>
        {error}
      </div>
    )}
  </div>
);

const TextareaField = ({ label, value, onChange, placeholder, optional }) => (
  <div style={{ padding: "10px" }}>
    <label
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: "#374151",
        display: "block",
        marginBottom: 6,
      }}
    >
      {label}{" "}
      {optional && (
        <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
      )}
    </label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 12px",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        fontSize: 13,
        color: "#111827",
        fontFamily: "inherit",
        resize: "vertical",
        outline: "none",
        lineHeight: 1.6,
      }}
    />
  </div>
);

/* ═══════════════════════════════════════════
   RECRUITMENT AGREEMENT FORM MODAL — new
   Converts the blank-field template into a
   typed UI form, then saves as T&C HTML
═══════════════════════════════════════════ */
// const DEFAULT_ANNEXURE = {
//   recruitmentFee: "8.33",
//   paymentDays: "35",
//   replacementDays: "90",
//   replacementDeadlineDays: "30",
//   noticePeriod: "30",
//   agreementValidity: "3 Years",
//   jurisdiction: "Bangalore, Karnataka",
// };

// function buildAgreementHTML(f, annexure) {
//   const blank = (val, fallback = "___________") =>
//     val && val.trim() ? `<strong>${val}</strong>` : `<u>${fallback}</u>`;
//   return `
// <div style="font-family:Georgia,serif;font-size:14px;line-height:1.8;color:#1a1a1a;max-width:800px;margin:0 auto;">
//   <h2 style="text-align:center;font-size:18px;font-weight:bold;margin-bottom:4px;">RECRUITMENT CONSULTANCY AGREEMENT</h2>
//   <p style="text-align:center;font-size:13px;color:#555;">This Agreement is made on ${blank(f.date)} at Bangalore, Karnataka.</p>

//   <p><strong>Between</strong></p>
//   <p>${blank(f.companyName, "Company Name")}, having its office at ${blank(f.companyAddress, "Company Address")}, hereinafter referred to as the <strong>"Company"</strong>;</p>
//   <p><strong>AND</strong></p>
//   <p>${blank(f.consultancyName, "Consultancy Name")}, having its office at ${blank(f.consultancyAddress, "Consultancy Address")}, hereinafter referred to as the <strong>"Consultant"</strong>.</p>

//   <h3 style="margin-top:20px;">1. PURPOSE OF AGREEMENT</h3>
//   <p>The Company appoints the Consultant on a non-exclusive basis to provide recruitment and talent acquisition services for positions assigned by the Company from time to time.</p>

//   <h3 style="margin-top:16px;">2. SCOPE OF SERVICES</h3>
//   <p>The Consultant shall source suitable candidates, conduct preliminary screening, coordinate interviews, and support closures as required. The Consultant shall not make commitments on behalf of the Company or share confidential information.</p>

//   <h3 style="margin-top:16px;">3. ENGAGEMENT MODEL</h3>
//   <p>This Agreement operates on a requisition basis. No exclusivity is granted unless specifically agreed in writing. The Company may work with multiple consultants simultaneously.</p>

//   <h3 style="margin-top:16px;">4. CONSULTANCY FEES</h3>
//   <p>The Company shall pay a professional fee of <strong>${annexure.recruitmentFee}% of Annual Gross Salary</strong> for each successfully placed candidate.</p>
//   <p>Fees are applicable only if the candidate joins, was first introduced by the Consultant, and was not already in the Company database.</p>

//   <h3 style="margin-top:16px;">5. PAYMENT TERMS</h3>
//   <p>Payment within <strong>${annexure.paymentDays} days</strong> of joining date and submission of valid invoice. Subject to TDS deductions. No advance payments.</p>

//   <h3 style="margin-top:16px;">6. FREE REPLACEMENT GUARANTEE</h3>
//   <p>If a candidate exits within <strong>${annexure.replacementDays} days</strong> of joining, the Consultant provides one replacement at no cost, to be delivered within <strong>${annexure.replacementDeadlineDays} days</strong>.</p>

//   <h3 style="margin-top:16px;">7–14. GENERAL TERMS</h3>
//   <p>Candidate Authenticity, Non-Solicitation (24 months post-term), Confidentiality, Data Privacy, Background Verification, Independent Contractor status, Limitation of Liability, and Indemnity — all as per standard terms.</p>

//   <h3 style="margin-top:16px;">15. TERMINATION</h3>
//   <p>Either party may terminate with <strong>${annexure.noticePeriod} days</strong> written notice. Immediate termination on misconduct, fraud, or confidentiality breach.</p>

//   <h3 style="margin-top:16px;">16–18. GOVERNING LAW & DISPUTES</h3>
//   <p>Governed by Indian law. Jurisdiction: <strong>${annexure.jurisdiction}</strong>. Disputes resolved by arbitration (Arbitration & Conciliation Act, 1996).</p>

//   <h3 style="margin-top:16px;">19. NOTICE DETAILS</h3>
//   <p><strong>Company:</strong> ${blank(f.companyContactName, "Contact Name")}, ${blank(f.companyEmail, "Email")}</p>
//   <p><strong>Consultant:</strong> ${blank(f.consultancyContactName, "Contact Name")}, ${blank(f.consultancyEmail, "Email")}</p>

//   <h3 style="margin-top:16px;">ANNEXURE 1 – STANDARD TERMS</h3>
//   <table style="width:100%;border-collapse:collapse;font-size:13px;margin-top:8px;">
//     <thead><tr style="background:#f3f4f6;"><th style="padding:8px 12px;border:1px solid #e5e7eb;text-align:left;">Particulars</th><th style="padding:8px 12px;border:1px solid #e5e7eb;text-align:left;">Details</th></tr></thead>
//     <tbody>
//       ${[
//         [
//           "Recruitment Fee",
//           `${annexure.recruitmentFee}% of Annual Gross Salary`,
//         ],
//         ["Payment Terms", `Within ${annexure.paymentDays} days of joining`],
//         ["Replacement Period", `${annexure.replacementDays} days`],
//         [
//           "Replacement to be made within",
//           `${annexure.replacementDeadlineDays} days`,
//         ],
//         ["Notice Period for Termination", `${annexure.noticePeriod} days`],
//         ["Agreement Validity", annexure.agreementValidity],
//         ["Jurisdiction", annexure.jurisdiction],
//         ["GST Applicability", "Extra as applicable"],
//         ["TDS Deduction", "As per Income Tax Act"],
//       ]
//         .map(
//           ([k, v]) =>
//             `<tr><td style="padding:7px 12px;border:1px solid #e5e7eb;">${k}</td><td style="padding:7px 12px;border:1px solid #e5e7eb;font-weight:600;">${v}</td></tr>`,
//         )
//         .join("")}
//     </tbody>
//   </table>

//   <div style="margin-top:28px;display:flex;justify-content:space-between;gap:40px;">
//     <div style="flex:1;border-top:1px solid #999;padding-top:8px;text-align:center;font-size:12px;color:#555;">
//       <strong>For the Company</strong><br/>
//       ${blank(f.companySignatoryName, "Authorised Signatory")}<br/>
//       ${blank(f.companySignatoryDesignation, "Designation")}
//     </div>
//     <div style="flex:1;border-top:1px solid #999;padding-top:8px;text-align:center;font-size:12px;color:#555;">
//       <strong>For the Recruitment Consultancy</strong><br/>
//       ${blank(f.consultancySignatoryName, "Authorised Signatory")}<br/>
//       ${blank(f.consultancySignatoryDesignation, "Designation")}
//     </div>
//   </div>
// </div>
//   `.trim();
// }

/* ═══════════════════════════════════════════
   MODALS
═══════════════════════════════════════════ */
const DecideModal = ({ conn, action, onConfirm, onClose }) => {
  const [busy, setBusy] = useState(false);
  const yes = action === "ACCEPTED";
  const name =
    conn.companyName ||
    conn.senderName ||
    conn.connectionCompanyName ||
    "This company";
  return (
    <Modal onClose={onClose} width={400}>
      <div style={{ padding: "10px", textAlign: "center" }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            margin: "0 auto 16px",
            background: yes ? "#dcfce7" : "#fee2e2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 24,
          }}
        >
          {yes ? "✓" : "✕"}
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#111827",
            marginBottom: 8,
          }}
        >
          {yes ? "Accept this connection?" : "Decline this connection?"}
        </div>
        <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 4 }}>
          {name}
        </div>
        {conn.moduleName && (
          <div style={{ fontSize: 12, color: "#9ca3af" }}>
            Module: {conn.moduleName}
          </div>
        )}
        {!yes && (
          <div style={{ fontSize: 12, color: "#dc2626", marginTop: 8 }}>
            ⚠ This cannot be undone.
          </div>
        )}
      </div>
      <ModalFoot>
        <Btn v="ghost" onClick={onClose}>
          Cancel
        </Btn>
        <Btn
          v={yes ? "success" : "danger"}
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
            } finally {
              setBusy(false);
            }
          }}
        >
          {yes ? "Yes, accept" : "Yes, decline"}
        </Btn>
      </ModalFoot>
    </Modal>
  );
};

const TCModal = ({
  title,
  sub,
  initText = "",
  initRemark = "",
  refHTML,
  refLabel,
  isResend,
  showDraft = true,
  onSubmit,
  onClose,
  showAgreementBuilder,
  onOpenBuilder,
}) => {
  const [text, setText] = useState(initText);
  const [remark, setRemark] = useState(initRemark);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async (isDraft) => {
    if (isBlank(text)) {
      setErr("Please write your terms & conditions.");
      return;
    }
    setBusy(true);
    await onSubmit(text, remark, isDraft);
    setBusy(false);
  };
  return (
    <Modal onClose={onClose} width={680}>
      <ModalHead title={title} sub={sub} onClose={onClose} />
      {isResend && (
        <div
          style={{
            margin: "10px",
            padding: "10px 14px",
            background: "#fef3c7",
            borderRadius: 8,
            fontSize: 13,
            color: "#d97706",
          }}
        >
          ⚠ Your previous terms were rejected. Update and resend below.
        </div>
      )}
      {showAgreementBuilder && (
        <div
          style={{
            margin: "10px",
            padding: "10px 14px",
            background: "#f0fdf4",
            borderRadius: 8,
            fontSize: 13,
            color: "#15803d",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <span>
            💡 Need a standard Recruitment Consultancy Agreement? Use our
            template builder.
          </span>
          <Btn v="green" sm onClick={onOpenBuilder}>
            📋 Use Template
          </Btn>
        </div>
      )}
      <div style={{ height: 16 }} />
      <TCViewer html={refHTML} label={refLabel} />
      <QuillField
        label="Terms & conditions"
        value={text}
        onChange={(v) => {
          setText(v);
          setErr("");
        }}
        placeholder="Write your terms & conditions…"
        error={err}
        required
      />
      <TextareaField
        label="Remark"
        value={remark}
        onChange={setRemark}
        placeholder="Optional note for the other party…"
        optional
      />
      <ModalFoot>
        <Btn v="ghost" onClick={onClose}>
          Cancel
        </Btn>
        {!isResend && showDraft && (
          <Btn v="outline" loading={busy} onClick={() => go(true)}>
            Save draft
          </Btn>
        )}
        <Btn v="primary" loading={busy} onClick={() => go(false)}>
          {busy ? "Generating PDF…" : isResend ? "Resend T&C" : "Send T&C"}
        </Btn>
      </ModalFoot>
    </Modal>
  );
};

const ReviewTCModal = ({ tc, onAccept, onReject, onCounter, onClose }) => {
  const [mode, setMode] = useState("accept");
  const [text, setText] = useState("");
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // PDF state — fetch when T&C was sent as a PDF
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const hasPdf = !!tc.moduleHistoryId;
  const theirText = tc.senderTerms || tc.documentText || "";
  const sub = `${tc.companyName || tc.senderName || tc.connectionCompanyName || ""}${tc.moduleName ? ` · ${tc.moduleName}` : ""}`;

  useEffect(() => {
    if (!hasPdf) return;
    setPdfLoading(true);
    setPdfError("");
    const hrUrl = `${process.env.REACT_APP_API_HR_URL}/connections/module-terms/file/${tc.moduleHistoryId}`;
    const cadminUrl = `${process.env.REACT_APP_API_CONSULTANT_ADMIN_URL}/connections/module-terms/file/${tc.moduleHistoryId}`;
    const tryFetch = (url) =>
      axios.get(url, { responseType: "json" }).then((r) => {
        const b64 = r.data?.data?.fileContent;
        if (!b64) throw new Error("No file content");
        const byteChars = atob(b64);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++)
          byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: "application/pdf" });
        return URL.createObjectURL(blob);
      });
    tryFetch(hrUrl)
      .catch(() => tryFetch(cadminUrl))
      .then((url) => {
        setPdfUrl(url);
        setPdfLoading(false);
      })
      .catch(() => {
        setPdfError("Could not load the T&C PDF.");
        setPdfLoading(false);
      });
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    }; // eslint-disable-line
  }, [tc.moduleHistoryId]); // eslint-disable-line

  return (
    <Modal onClose={onClose} width={820}>
      <ModalHead
        title="Review Terms & Conditions"
        sub={sub}
        onClose={onClose}
      />

      {/* ── T&C content: PDF (preferred) or HTML fallback ── */}
      <div style={{ padding: "12px 16px 0" }}>
        {hasPdf && (
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              overflow: "hidden",
              marginBottom: 12,
            }}
          >
            {pdfLoading && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  padding: "32px 0",
                  color: "#6b7280",
                  fontSize: 13,
                }}
              >
                <Spin dark /> Loading PDF…
              </div>
            )}
            {pdfError && !pdfLoading && (
              <div
                style={{
                  background: "#fef2f2",
                  padding: "12px 16px",
                  color: "#dc2626",
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                ⚠ {pdfError}
              </div>
            )}
            {pdfUrl && !pdfLoading && (
              <iframe
                src={pdfUrl}
                title="Terms & Conditions PDF"
                style={{
                  width: "100%",
                  height: "55vh",
                  border: "none",
                  display: "block",
                }}
              />
            )}
          </div>
        )}
        {/* HTML fallback when no PDF */}
        {!hasPdf && (
          <TCViewer html={theirText} label="Their terms & conditions" />
        )}
      </div>
      <div style={{ padding: "10px" }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#374151",
            marginBottom: 8,
          }}
        >
          Your response
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { k: "accept", l: "✓ Accept" },
            { k: "reject", l: "✕ Reject" },
            { k: "counter", l: "↕ Counter-propose" },
          ].map((o) => (
            <button
              key={o.k}
              onClick={() => setMode(o.k)}
              style={{
                padding: "6px 14px",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                border: `1.5px solid ${mode === o.k ? "#2563eb" : "#d1d5db"}`,
                background: mode === o.k ? "#2563eb" : "#fff",
                color: mode === o.k ? "#fff" : "#374151",
                fontFamily: "inherit",
              }}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>
      {mode === "accept" && (
        <div
          style={{
            margin: "10px",
            padding: "10px 14px",
            background: "#eff6ff",
            borderRadius: 8,
            fontSize: 13,
            color: "#2563eb",
          }}
        >
          ✓ Accepting means you agree to the terms above.
        </div>
      )}
      {mode === "reject" && (
        <div
          style={{
            margin: "10px",
            padding: "10px 14px",
            background: "#fef3c7",
            borderRadius: 8,
            fontSize: 13,
            color: "#d97706",
          }}
        >
          ⚠ Rejecting notifies the other party and cannot be undone.
        </div>
      )}
      <TextareaField
        label={mode === "reject" ? "Reason" : "Remark"}
        value={remark}
        onChange={setRemark}
        placeholder={
          mode === "reject" ? "State your reason…" : "Optional note…"
        }
        optional
      />
      {mode === "counter" && (
        <QuillField
          label="Your counter-terms"
          value={text}
          onChange={(v) => {
            setText(v);
            setErr("");
          }}
          placeholder="Write your counter-terms…"
          error={err}
          required
        />
      )}
      <ModalFoot>
        <Btn v="ghost" onClick={onClose}>
          Cancel
        </Btn>
        {mode === "accept" && (
          <Btn
            v="success"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onAccept(remark);
              } finally {
                setBusy(false);
              }
            }}
          >
            Accept terms
          </Btn>
        )}
        {mode === "reject" && (
          <Btn
            v="danger"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onReject(remark);
              } finally {
                setBusy(false);
              }
            }}
          >
            Reject terms
          </Btn>
        )}
        {mode === "counter" && (
          <Btn
            v="primary"
            loading={busy}
            onClick={async () => {
              if (isBlank(text)) {
                setErr("Write your counter-terms.");
                return;
              }
              setBusy(true);
              try {
                await onCounter(text, remark);
              } finally {
                setBusy(false);
              }
            }}
          >
            Send counter-terms
          </Btn>
        )}
      </ModalFoot>
    </Modal>
  );
};

const AcceptModal = ({ item, onAccept, onReject, onClose }) => {
  const [mode, setMode] = useState("accept");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  // PDF state — used when T&C was sent as a PDF (no HTML body)
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const body =
    item.receiverTerms || item.documentText || item.senderTerms || "";
  const hasPdf = !!item.moduleHistoryId;
  const sub = `${item.companyName || item.connectionCompanyName || ""}${item.moduleName ? ` · ${item.moduleName}` : ""}`;

  // Fetch PDF inline when there's a moduleHistoryId
  useEffect(() => {
    if (!hasPdf) return;
    setPdfLoading(true);
    setPdfError("");
    const hrUrl = `${process.env.REACT_APP_API_HR_URL}/connections/module-terms/file/${item.moduleHistoryId}`;
    const cadminUrl = `${process.env.REACT_APP_API_CONSULTANT_ADMIN_URL}/connections/module-terms/file/${item.moduleHistoryId}`;
    const tryFetch = (url) =>
      axios.get(url, { responseType: "json" }).then((r) => {
        const b64 = r.data?.data?.fileContent;
        if (!b64) throw new Error("No file content");
        const byteChars = atob(b64);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++)
          byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: "application/pdf" });
        return URL.createObjectURL(blob);
      });
    tryFetch(hrUrl)
      .catch(() => tryFetch(cadminUrl))
      .then((url) => {
        setPdfUrl(url);
        setPdfLoading(false);
      })
      .catch(() => {
        setPdfError("Could not load the T&C PDF.");
        setPdfLoading(false);
      });
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    }; // eslint-disable-line
  }, [item.moduleHistoryId]); // eslint-disable-line

  return (
    <Modal onClose={onClose} width={820}>
      <ModalHead
        title="Review Terms & Conditions"
        sub={sub}
        onClose={onClose}
      />

      {/* ── T&C content: PDF (preferred) or HTML fallback ── */}
      <div style={{ padding: "12px 16px 0" }}>
        {/* PDF viewer */}
        {hasPdf && (
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              overflow: "hidden",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                background: "#f9fafb",
                padding: "8px 14px",
                fontSize: 11,
                fontWeight: 700,
                color: "#6b7280",
                letterSpacing: "0.05em",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>📄 TERMS & CONDITIONS</span>
              {pdfUrl && (
                <a
                  href={pdfUrl}
                  download={`TC_${item.moduleHistoryId}.pdf`}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#2563eb",
                    textDecoration: "none",
                  }}
                >
                  ⬇ Download
                </a>
              )}
            </div>
            {pdfLoading && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  padding: "32px 0",
                  color: "#6b7280",
                  fontSize: 13,
                }}
              >
                <Spin dark /> Loading PDF…
              </div>
            )}
            {pdfError && !pdfLoading && (
              <div
                style={{
                  padding: "16px",
                  color: "#dc2626",
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                ⚠ {pdfError}
              </div>
            )}
            {pdfUrl && !pdfLoading && (
              <iframe
                src={pdfUrl}
                title="Terms & Conditions PDF"
                style={{
                  width: "100%",
                  height: "50vh",
                  border: "none",
                  display: "block",
                }}
              />
            )}
          </div>
        )}

        {/* HTML fallback (when no PDF) */}
        {!hasPdf && <TCViewer html={body} label="Terms & conditions" />}
      </div>

      {/* ── Accept / Reject toggle ── */}
      <div style={{ padding: "10px 16px 0" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { k: "accept", l: "✓ Accept" },
            { k: "reject", l: "✕ Reject" },
          ].map((o) => (
            <button
              key={o.k}
              onClick={() => setMode(o.k)}
              style={{
                padding: "6px 14px",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                border: `1.5px solid ${mode === o.k ? (o.k === "accept" ? "#15803d" : "#dc2626") : "#d1d5db"}`,
                background:
                  mode === o.k
                    ? o.k === "accept"
                      ? "#15803d"
                      : "#dc2626"
                    : "#fff",
                color: mode === o.k ? "#fff" : "#374151",
                fontFamily: "inherit",
              }}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {mode === "reject" && (
        <div
          style={{
            margin: "10px 16px",
            padding: "10px 14px",
            background: "#fef3c7",
            borderRadius: 8,
            fontSize: 13,
            color: "#d97706",
          }}
        >
          ⚠ This will notify the other party and cannot be undone.
        </div>
      )}
      <TextareaField
        label={mode === "accept" ? "Remark" : "Reason"}
        value={reason}
        onChange={setReason}
        placeholder={
          mode === "accept" ? "Optional note…" : "State your reason…"
        }
        optional
      />
      <ModalFoot>
        <Btn v="ghost" onClick={onClose}>
          Cancel
        </Btn>
        {mode === "accept" ? (
          <Btn
            v="success"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onAccept(reason);
              } finally {
                setBusy(false);
              }
            }}
          >
            Accept terms
          </Btn>
        ) : (
          <Btn
            v="danger"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onReject(reason);
              } finally {
                setBusy(false);
              }
            }}
          >
            Reject terms
          </Btn>
        )}
      </ModalFoot>
    </Modal>
  );
};

/* ═══════════════════════════════════════════
   PDF VIEWER MODAL
   Fetches the T&C PDF from the API using moduleHistoryId
   and renders it inline in a popup.
═══════════════════════════════════════════ */
const PDFViewerModal = ({ moduleHistoryId, companyName, onClose }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { isConsultant } = useWorkspace();

  const baseUrl = isConsultant
    ? process.env.REACT_APP_API_CONSULTANT_ADMIN_URL
    : process.env.REACT_APP_API_HR_URL;

  useEffect(() => {
    if (!moduleHistoryId) return;

    setLoading(true);
    setError("");

    axios
      .get(`${baseUrl}/connections/module-terms/file/${moduleHistoryId}`, {
        responseType: "json",
      })
      .then((r) => {
        const b64 = r.data?.data?.fileContent;

        if (!b64) {
          throw new Error("No file content");
        }

        const byteChars = atob(b64);
        const byteArr = new Uint8Array(byteChars.length);

        for (let i = 0; i < byteChars.length; i++) {
          byteArr[i] = byteChars.charCodeAt(i);
        }

        const blob = new Blob([byteArr], {
          type: "application/pdf",
        });

        setPdfUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        setError("Could not load the T&C file.");
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [moduleHistoryId, isConsultant, baseUrl]);

  return (
    <Modal onClose={onClose} width={840}>
      <ModalHead
        title="Terms & Conditions — PDF"
        sub={companyName || `Module History #${moduleHistoryId}`}
        onClose={onClose}
      />
      <div style={{ padding: "12px 16px 16px" }}>
        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "48px 0",
              color: "#6b7280",
              fontSize: 14,
            }}
          >
            <Spin dark /> Loading PDF…
          </div>
        )}
        {error && !loading && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 10,
              padding: "16px 20px",
              color: "#dc2626",
              fontSize: 13,
              textAlign: "center",
            }}
          >
            ⚠ {error}
          </div>
        )}
        {pdfUrl && !loading && (
          <>
            <iframe
              src={pdfUrl}
              title="Terms & Conditions PDF"
              style={{
                width: "100%",
                height: "65vh",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            />
            <div
              style={{
                marginTop: 10,
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
              }}
            >
              <a
                href={pdfUrl}
                download={`TC_${moduleHistoryId}.pdf`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 14px",
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 600,
                  border: "1.5px solid #2563eb",
                  background: "#eff6ff",
                  color: "#2563eb",
                  textDecoration: "none",
                }}
              >
                ⬇ Download PDF
              </a>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const AssignModal = ({
  moduleHistoryId,
  connectionName,
  moduleName,
  currentEmployee,
  connectionId,
  fetchData,
  onClose,
}) => {
  const [emps, setEmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios
      .get(`${CADMIN}/employees/consultants`)
      .then((r) => setEmps(r.data.data || []))
      .catch(() => toast.error("Failed to load employees."))
      .finally(() => setLoading(false));
  }, []);
  const filtered = emps.filter((e) => {
    const q = search.toLowerCase();
    const n = `${e.firstName || ""} ${e.lastName || ""}`.toLowerCase();
    return n.includes(q) || (e.companyEmail || "").toLowerCase().includes(q);
  });
  const go = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await axios.put(`${CADMIN}/module/assign/employee`, {
        moduleHistoryId,
        employeeId: sel.empId,
      });
      toast.success(`${sel.firstName} assigned!`);
      if (fetchData) fetchData();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose} width={480}>
      <ModalHead
        title="Assign employee"
        sub={`${connectionName || `#${connectionId}`}${moduleName ? ` · ${moduleName}` : ""}`}
        onClose={onClose}
      />
      {currentEmployee && (
        <div
          style={{
            margin: "10px",
            padding: "10px 14px",
            background: "#f3f4f6",
            borderRadius: 8,
            fontSize: 13,
            color: "#374151",
          }}
        >
          👤 Currently: <strong>{currentEmployee}</strong>
        </div>
      )}
      <div style={{ padding: "10px" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "8px 12px",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            fontSize: 13,
            outline: "none",
          }}
        />
      </div>
      <div style={{ maxHeight: 280, overflowY: "auto", padding: "0 16px" }}>
        {loading ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <EmptyCard heading="No employees found" />
        ) : (
          filtered.map((emp) => {
            const name = `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
            const on = sel?.empId === emp.empId;
            return (
              <div
                key={emp.empId}
                onClick={() => setSel(on ? null : emp)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  borderRadius: 10,
                  cursor: "pointer",
                  background: on ? "#eff6ff" : "transparent",
                  marginBottom: 2,
                  transition: "background .15s",
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    border: `1.5px solid ${on ? "#2563eb" : "#d1d5db"}`,
                    background: on ? "#2563eb" : "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  {on && "✓"}
                </div>
                <Avatar name={name} sz={32} />
                <div>
                  <div
                    style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}
                  >
                    {name || "—"}
                  </div>
                  {emp.companyEmail && (
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      {emp.companyEmail}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      <ModalFoot>
        <Btn v="ghost" onClick={onClose}>
          Cancel
        </Btn>
        <Btn v="primary" loading={busy} disabled={!sel} onClick={go}>
          {sel ? `Assign ${sel.firstName}` : "Select an employee"}
        </Btn>
      </ModalFoot>
    </Modal>
  );
};

/* ═══════════════════════════════════════════
   SOCIAL CARD COMPONENTS
═══════════════════════════════════════════ */
const RequestCard = ({
  name,
  logo,
  logoType,
  module,
  date,
  status,
  actions,
  tcFlow,
  info,
  highlight,
}) => (
  <div
    style={{
      background: "#fff",
      border: highlight ? "1.5px solid #2563eb" : "1px solid #e5e7eb",
      borderRadius: 14,
      padding: "10px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
      boxShadow: highlight ? "0 0 0 3px #eff6ff" : "0 1px 3px rgba(0,0,0,.06)",
      position: "relative",
      transition: "box-shadow .2s",
    }}
  >
    {highlight && (
      <div
        style={{
          position: "absolute",
          top: -1,
          left: 16,
          background: "#2563eb",
          color: "#fff",
          fontSize: 10,
          fontWeight: 700,
          padding: "2px 8px",
          borderRadius: "0 0 6px 6px",
          letterSpacing: "0.04em",
        }}
      >
        NEEDS YOUR ACTION
      </div>
    )}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        paddingTop: highlight ? 8 : 0,
      }}
    >
      <Avatar name={name} logo={logo} logoType={logoType} sz={44} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 14,
            color: "#111827",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name || "—"}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 2,
            flexWrap: "wrap",
          }}
        >
          {module && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 500,
                color: "#6b7280",
                background: "#f3f4f6",
                borderRadius: 20,
                padding: "1px 8px",
              }}
            >
              {module}
            </span>
          )}
          {date && (
            <span style={{ fontSize: 11, color: "#9ca3af" }}>
              {fmtDate(date)}
            </span>
          )}
          {info && <InfoTooltip text={info} />}
        </div>
      </div>
      <StatusPill status={status} />
    </div>
    {tcFlow && (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 12px",
          background: "#f9fafb",
          borderRadius: 8,
        }}
      >
        <span style={{ fontSize: 11, color: "#9ca3af" }}>T&C flow:</span>
        {tcFlow.sender && <StatusPill status={tcFlow.sender} />}
        {tcFlow.sender && tcFlow.receiver && (
          <span style={{ fontSize: 11, color: "#d1d5db" }}>→</span>
        )}
        {tcFlow.receiver && <StatusPill status={tcFlow.receiver} />}
        {!tcFlow.sender && !tcFlow.receiver && (
          <span style={{ fontSize: 11, color: "#9ca3af" }}>
            No T&C exchanged yet
          </span>
        )}
      </div>
    )}
    {actions && actions.length > 0 && (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>
    )}
  </div>
);

const CompanyInviteCard = ({ tenant, selMod, onInvite, inviting, sent }) => {
  const st = sent ? "INVITED" : tenant.connectionModuleAggrementStatus || null;
  const connected = ["REQUEST_ACCEPTED", "ACCEPTED"].includes(st);
  const pending = ["INVITED", "REQUESTED"].includes(st);
  const canInvite = !st && !sent;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        height: "100%",
        transition: "all 0.2s ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Avatar
          name={tenant.companyName}
          logo={tenant.companyLogo}
          logoType={tenant.companyLogoType}
          sz={40}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: "#111827",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {tenant.companyName}
          </div>
          {tenant.subDomain && (
            <div
              style={{
                fontSize: 11,
                color: "#9ca3af",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {tenant.subDomain}
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginTop: "auto",
          borderRadius: 10,
        }}
      >
        <div style={{ minWidth: 0 }}>{st && <StatusPill status={st} />}</div>
        <LockedButton
          codes={[RECRUITMENT.CONNECTIONS_SHARE_REQUIREMENT]}
          className={`cn-btn cn-btn--sm cn-btn--${connected ? "green" : pending ? "neutral" : "primary"}`}
          disabled={!canInvite}
          onClick={() => canInvite && onInvite(tenant)}
        >
          {connected ? "✓ Connected" : pending ? "⏳ Pending" : "Invite"}
        </LockedButton>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
export default function Connections() {
  const { isConsultant } = useWorkspace();
  return isConsultant ? <ConsultantView /> : <CompanyView />;
}

/* ═══════════════════════════════════════════
   COMPANY VIEW
═══════════════════════════════════════════ */
function CompanyView() {
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState("");

  const [tenants, setTenants] = useState([]);
  const [mods, setMods] = useState([]);
  const [selMod, setSelMod] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [sentIds, setSentIds] = useState(new Set());
  const [loadMods, setLoadMods] = useState(true);
  const [loadTens, setLoadTens] = useState(false);

  const [connections, setConnections] = useState([]);
  const [loadConn, setLoadConn] = useState(true);

  const [recvInvites, setRecvInvites] = useState([]);
  const [loadRI, setLoadRI] = useState(true);
  const [tcReceived, setTcReceived] = useState([]);
  const [loadTCR, setLoadTCR] = useState(true);

  const loadConnections = useCallback(() => {
    setLoadConn(true);
    axios
      .get(`${HR_BASE}/consultancy`)
      .then((r) => setConnections((r.data.data || []).map(normConsultancy)))
      .catch(() => toast.error("Failed to load connections."))
      .finally(() => setLoadConn(false));
  }, []);
  const loadRecvInvites = useCallback(() => {
    setLoadRI(true);
    axios
      .get(`${HR_BASE}/modules/invited`)
      .then((r) => setRecvInvites(normDeep(r.data.data || [])))
      .catch(console.error)
      .finally(() => setLoadRI(false));
  }, []);
  const loadTCReceived = useCallback(() => {
    setLoadTCR(true);
    axios
      .get(`${HR_BASE}/terms_and_condition/received`)
      .then((r) => setTcReceived(normDeep(r.data.data || [])))
      .catch(console.error)
      .finally(() => setLoadTCR(false));
  }, []);

  useEffect(() => {
    setLoadMods(true);
    axios
      .get(`${HR_BASE}/modules`)
      .then((r) => {
        const l = r.data.data || [];
        setMods(l);
        if (l.length) {
          const def =
            l.find((m) => m.connectionModuleName !== "Recruitment") || l[0];
          setSelMod(def);
        }
      })
      .catch(() => toast.error("Failed to load modules."))
      .finally(() => setLoadMods(false));
    loadConnections();
    loadRecvInvites();
    loadTCReceived();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!selMod) return;
    setLoadTens(true);
    setTenants([]);
    setSentIds(new Set());
    axios
      .get(`${HR_BASE}/${selMod.moduleId}/tenants`)
      .then((r) => setTenants(normDeep(r.data.data || [])))
      .catch(() => toast.error("Failed to load companies."))
      .finally(() => setLoadTens(false));
  }, [selMod]);

  const invite = async (t) => {
    setInviting(t.tenantRegId);
    try {
      await axios.post(HR_BASE, {
        tenantRegId: t.tenantRegId,
        moduleInviteRequestDTOList: [
          { moduleName: normModule(selMod.connectionModuleName) },
        ],
      });
      toast.success(`Invite sent to ${t.companyName}!`);
      setSentIds((p) => new Set([...p, t.tenantRegId]));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    } finally {
      setInviting(null);
    }
  };

  const decideConn = async (mhId, status) => {
    try {
      await axios.put(`${HR_BASE}/module/accept/reject`, {
        moduleHistoryId: mhId,
        decision: status,
      });
      toast.success(
        `Connection ${status === "ACCEPTED" ? "accepted" : "rejected"}!`,
      );
      loadConnections();
      loadRecvInvites();
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    }
  };

  const sendTC = async (mhId, t, r, d) => {
    try {
      const pdfBlob = await generatePDFBlob(t);
      await submitAgreement({
        moduleHistoryId: mhId,
        pdfBlob,
        senderDocumentStatus: d ? "DRAFT" : "SENT",
        isDraft: !!d,
        hrBase: HR_BASE,
      });
      toast.success(d ? "Draft saved!" : "T&C sent!");
      loadConnections();
      loadTCReceived();
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    }
  };
  const resendTC = async (mhId, t, r) => {
    try {
      const pdfBlob = await generatePDFBlob(t);
      await submitAgreement({
        moduleHistoryId: mhId,
        pdfBlob,
        senderDocumentStatus: "RESENT",
        isDraft: false,
        hrBase: HR_BASE,
      });
      toast.success("T&C resent!");
      loadConnections();
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    }
  };
  const acceptTC = async (mhId, remark) => {
    try {
      await axios.put(`${HR_BASE}/receiver/terms-decision`, {
        moduleHistoryId: mhId,
        receiverDocumentStatus: "RECEIVED_ACCEPTED",
        ...(remark ? { remark } : {}),
      });
      toast.success("T&C accepted!");
      loadTCReceived();
      loadConnections();
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    }
  };
  const rejectTC = async (mhId, remark) => {
    try {
      await axios.put(`${HR_BASE}/receiver/terms-decision`, {
        moduleHistoryId: mhId,
        receiverDocumentStatus: "RECEIVED_REJECTED",
        ...(remark ? { remark } : {}),
      });
      toast.success("T&C rejected.");
      loadTCReceived();
      loadConnections();
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    }
  };

  const pendingInvites = recvInvites.filter(
    (i) =>
      ![
        "ACCEPTED",
        "REQUEST_ACCEPTED",
        "REJECTED",
        "REQUEST_REJECTED",
      ].includes(i.moduleAggrementStatus),
  );
  const activeConns = connections.filter((c) =>
    ["ACCEPTED", "REQUEST_ACCEPTED"].includes(c.moduleAggrementStatus),
  );
  const pendingConns = connections.filter((c) =>
    ["INVITED", "REQUESTED"].includes(c.moduleAggrementStatus),
  );

  // Single source of truth for a connection's T&C state. Used for sorting,
  // counting, and rendering the My Connections cards — this is also where
  // "Send Terms & Conditions" used to live as its own section/dataset; it's
  // folded in here so there's exactly one place per connection that decides
  // what action (if any) is needed, instead of two endpoints disagreeing.
  const getConnFlags = (c) => {
    const canDecide = ["INVITED", "REQUESTED"].includes(
      c.moduleAggrementStatus,
    );
    const isConn = ["ACCEPTED", "REQUEST_ACCEPTED"].includes(
      c.moduleAggrementStatus,
    );
    const isDead = ["REJECTED", "REQUEST_REJECTED"].includes(
      c.moduleAggrementStatus,
    );
    const isSettled =
      c.senderDocumentStatus === "SENT_ACCEPTED" ||
      c.receiverDocumentStatus === "RECEIVED_ACCEPTED";
    const canResend = !isSettled && c.senderDocumentStatus === "SENT_REJECTED";
    const isDraft = c.senderDocumentStatus === "DRAFT";
    // canSend: connected + not settled + not a resend case, and either:
    //   - nothing sent yet
    //   - saved draft
    //   - consultancy rejected our T&C (senderDocumentStatus = RECEIVED_REJECTED)
    //   - we rejected their incoming T&C (receiverDocumentStatus = RECEIVED_REJECTED)
    const canSend =
      isConn &&
      !isSettled &&
      !canResend &&
      (!c.senderDocumentStatus ||
        isDraft ||
        c.senderDocumentStatus === "RECEIVED_REJECTED" ||
        c.receiverDocumentStatus === "RECEIVED_REJECTED");
    const isWaiting =
      isConn &&
      !isSettled &&
      !canResend &&
      !canSend &&
      ["SENT", "RESENT", "RECEIVED", "RE_RECEIVED"].includes(
        c.senderDocumentStatus,
      );
    const hasPdf =
      !!c.moduleHistoryId &&
      (c.senderDocumentStatus || c.receiverDocumentStatus);
    const needsAction = canDecide || canResend || canSend;
    // Lower rank = shown first. Anything needing action floats to the top;
    // settled/declined connections sink to the bottom.
    const rank = canDecide
      ? 0
      : canResend
        ? 1
        : canSend
          ? 2
          : isWaiting
            ? 3
            : isSettled
              ? 4
              : isDead
                ? 6
                : 5;
    return {
      canDecide,
      isConn,
      isDead,
      isSettled,
      canResend,
      isDraft,
      canSend,
      isWaiting,
      hasPdf,
      needsAction,
      rank,
    };
  };

  const sortedConnections = [...connections].sort(
    (a, b) => getConnFlags(a).rank - getConnFlags(b).rank,
  );
  const connsNeedTC = connections.filter((c) => {
    const f = getConnFlags(c);
    return f.canResend || f.canSend;
  });
  const tcToReview = tcReceived.filter(
    (r) => r.receiverDocumentStatus !== "RECEIVED_ACCEPTED",
  );
  const totalActions =
    pendingInvites.length +
    pendingConns.length +
    connsNeedTC.length +
    tcToReview.length;

  const share = () =>
    navigator.clipboard
      .writeText("https://www.ourdept.com/modules")
      .then(() => toast.success("Link copied!"))
      .catch(() => toast.error("Failed to copy."));

  const filteredTenants = tenants.filter((t) => {
    const q = search.toLowerCase();
    return (
      (t.companyName || "").toLowerCase().includes(q) ||
      (t.subDomain || "").toLowerCase().includes(q)
    );
  });

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f9fafb",
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      <style>{`
        @keyframes cn-spin { to { transform: rotate(360deg); } }
        @keyframes cn-modal-in { from { opacity:0; transform:scale(0.96) translateY(8px); } to { opacity:1; transform:none; } }
        @keyframes cn-fade-up { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }
        * { box-sizing: border-box; }
      `}</style>

      {/* ── Page Header ── */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #e5e7eb",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 100,
          gap: 16,
        }}
      >
        {/* Left — title */}
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#9ca3af",
              letterSpacing: "0.06em",
            }}
          >
            Company {/* or Consultancy */}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: "#111827",
                margin: 0,
                letterSpacing: "-0.4px",
              }}
            >
              Connections
            </h1>
            {totalActions > 0 && (
              <span
                style={{
                  background: "#dc2626",
                  color: "#fff",
                  borderRadius: 12,
                  padding: "2px 9px",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {totalActions} need action
              </span>
            )}
          </div>
        </div>

        {/* Right — patriotic banner, horizontal */}
        <div
          style={{
            flex: 1,
            background:
              "linear-gradient(135deg,#fff7ed 0%,#fff 50%,#f0fdf4 100%)",
            border: "1.5px solid #ff9933",
            borderRadius: 10,
            padding: "7px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 2px 8px rgba(255,153,51,0.15)",
          }}
        >
          {/* Tricolor left stripe */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: 3,
              background:
                "linear-gradient(180deg,#ff9933 33%,#fff 33%,#fff 66%,#138808 66%)",
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              paddingLeft: 8,
            }}
          >
            <span style={{ fontSize: 20, flexShrink: 0 }}>🇮🇳</span>
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: "#111827",
                  lineHeight: 1.2,
                }}
              >
                Help India's Businesses Grow Stronger
              </div>
              <div style={{ fontSize: 11, color: "#4b5563", lineHeight: 1.4 }}>
                Our <strong>Consultancy Module is 100% free</strong> —
                connecting Indian businesses with the best recruitment partners.
              </div>
            </div>
          </div>

          <button
            onClick={share}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              border: "1.5px solid #ff9933",
              background: "#fff7ed",
              color: "#92400e",
              cursor: "pointer",
              fontFamily: "inherit",
              whiteSpace: "nowrap",
              flexShrink: 0,
              transition: "all .15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                "0 3px 10px rgba(255,153,51,0.35)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "";
              e.currentTarget.style.transform = "";
            }}
          >
            🇮🇳 Share Free · No credit card
          </button>
        </div>
      </div>

      {/* ── Info bar ── */}
      <div
        style={{
          background: "#eff6ff",
          borderBottom: "1px solid #dbeafe",
          padding: "10px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ fontSize: 20 }}>ℹ️</span>
        <span style={{ fontSize: 13, color: "#1d4ed8" }}>
          This is your <strong>Connections hub</strong> — find partner
          companies, send connection invites, and exchange Terms & Conditions
          all in one place. Scroll down to see everything.
        </span>
      </div>

      <div
        style={{
          padding: "10px",
          display: "flex",
          flexDirection: "column",
          gap: 32,
          margin: "0 auto",
        }}
      >
        {/* SECTION 1 */}
        <section style={{ animation: "cn-fade-up .35s ease both" }}>
          <SectionHeader
            icon="🔍"
            title="Find Companies to Connect With"
            accent="#2563eb"
            info="Browse companies and send them a connection invite. They'll get notified and can accept or decline."
          />

          <div className="cone-stats-row">
            {[
              {
                label: "Available companies",
                sub: "Total companies available",
                value: tenants.length,
                tone: "blue",
              },
              {
                label: "Already connected",
                sub: "Accepted connections",
                value: tenants.filter((t) =>
                  ["REQUEST_ACCEPTED", "ACCEPTED"].includes(
                    t.connectionModuleAggrementStatus,
                  ),
                ).length,
                tone: "green",
              },
              {
                label: "Invites sent this session",
                sub: "Current session activity",
                value: sentIds.size,
                tone: "violet",
              },
            ].map((stat) => (
              <article
                key={stat.label}
                className={`cand-stat cand-stat--${stat.tone}`}
              >
                <span className="cand-stat__bar" />
                <div className="cand-stat__body">
                  <div className="cand-stat__text">
                    <p className="cand-stat__label">{stat.label}</p>
                    <p className="cand-stat__sub">{stat.sub}</p>
                  </div>
                  <h3 className="cand-stat__value">{stat.value}</h3>
                </div>
              </article>
            ))}
          </div>

          {loadMods ? (
            <Loading text="Loading modules…" />
          ) : !mods.length ? (
            <EmptyCard heading="No modules available" />
          ) : (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "14px 16px",
                  borderBottom: "1px solid #f3f4f6",
                  background: "#fafafa",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#9ca3af",
                    letterSpacing: "0.05em",
                    marginBottom: 8,
                  }}
                >
                  Select Module
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {mods.map((m) => {
                    const on = selMod?.moduleId === m.moduleId;
                    const isComingSoon =
                      m.connectionModuleName === "Recruitment";
                    return (
                      <button
                        key={m.moduleId}
                        disabled={isComingSoon}
                        onClick={() => !isComingSoon && setSelMod(m)}
                        title={
                          isComingSoon
                            ? "Beta version — coming soon"
                            : undefined
                        }
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 14px",
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: isComingSoon ? "not-allowed" : "pointer",
                          border: `1.5px solid ${
                            isComingSoon
                              ? "#e5e7eb"
                              : on
                                ? "#2563eb"
                                : "#d1d5db"
                          }`,
                          background: isComingSoon
                            ? "#f3f4f6"
                            : on
                              ? "#2563eb"
                              : "#fff",
                          color: isComingSoon
                            ? "#9ca3af"
                            : on
                              ? "#fff"
                              : "#374151",
                          opacity: isComingSoon ? 0.7 : 1,
                          fontFamily: "inherit",
                        }}
                      >
                        {m.connectionModuleName}
                        {isComingSoon && (
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              padding: "1px 6px",
                              borderRadius: 8,
                              background: "#e5e7eb",
                              color: "#6b7280",
                            }}
                          >
                            BETA · COMING SOON
                          </span>
                        )}
                        {!isComingSoon && on && " ✓"}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="🔍  Search by company name or domain…"
                  style={{
                    width: "100%",
                    padding: "9px 14px",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    fontSize: 13,
                    outline: "none",
                    background: "#f9fafb",
                  }}
                />
              </div>
              <div>
                {loadTens ? (
                  <Loading text="Loading companies…" />
                ) : filteredTenants.length === 0 ? (
                  <EmptyCard heading="No companies found" icon="🏢" />
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(280px, 1fr))",
                      gap: 12,
                      padding: 12,
                    }}
                  >
                    {filteredTenants.map((t) => (
                      <CompanyInviteCard
                        key={t.tenantRegId}
                        tenant={t}
                        selMod={selMod}
                        onInvite={invite}
                        inviting={inviting}
                        sent={sentIds.has(t.tenantRegId)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* SECTION 2 — RECEIVED INVITES */}
        <section style={{ animation: "cn-fade-up .4s ease both" }}>
          <SectionHeader
            icon="📨"
            title="Invites You Received"
            count={
              pendingInvites.length > 0
                ? `${pendingInvites.length} need response`
                : recvInvites.length
            }
            accent="#d97706"
            info="Companies have sent you connection invites. Accept to start collaborating, or decline if you're not interested."
          />
          {loadRI ? (
            <Loading />
          ) : recvInvites.length === 0 ? (
            <EmptyCard
              icon="📭"
              heading="No invites received yet"
              note="When a company invites you, it'll show up here."
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 12,
              }}
            >
              {recvInvites.map((i) => {
                const isPend = ![
                  "ACCEPTED",
                  "REQUEST_ACCEPTED",
                  "REJECTED",
                  "REQUEST_REJECTED",
                ].includes(i.moduleAggrementStatus);
                return (
                  <RequestCard
                    key={i.moduleHistoryId}
                    name={i.companyName}
                    logo={i.companyLogo}
                    logoType={i.companyLogoType}
                    module={i.moduleName}
                    date={i.sentOn}
                    status={i.moduleAggrementStatus}
                    highlight={isPend}
                    info={
                      isPend
                        ? "This company wants to connect with you. Accept to start exchanging T&C and working together."
                        : undefined
                    }
                    actions={
                      isPend
                        ? [
                            <Btn
                              key="a"
                              v="success"
                              sm
                              onClick={() =>
                                setModal({
                                  type: "decide",
                                  conn: i,
                                  action: "ACCEPTED",
                                  onConfirm: () =>
                                    decideConn(i.moduleHistoryId, "ACCEPTED"),
                                })
                              }
                            >
                              ✓ Accept
                            </Btn>,
                            <Btn
                              key="r"
                              v="red"
                              sm
                              onClick={() =>
                                setModal({
                                  type: "decide",
                                  conn: i,
                                  action: "REJECTED",
                                  onConfirm: () =>
                                    decideConn(i.moduleHistoryId, "REJECTED"),
                                })
                              }
                            >
                              ✕ Decline
                            </Btn>,
                          ]
                        : [
                            <span
                              key="s"
                              style={{ fontSize: 12, color: "#9ca3af" }}
                            >
                              {i.moduleAggrementStatus?.includes("REJECTED")
                                ? "You declined this invite"
                                : "You accepted this invite ✓"}
                            </span>,
                          ]
                    }
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* SECTION 3 — MY CONNECTIONS (includes sending/resending T&C) */}
        <section style={{ animation: "cn-fade-up .45s ease both" }}>
          <SectionHeader
            icon="🤝"
            title="My Connections"
            count={
              connsNeedTC.length > 0
                ? `${connsNeedTC.length} need T&C action`
                : connections.length
            }
            accent="#15803d"
            info="All companies you've connected with. Send, resend, or check on Terms & Conditions right from each card. Cards that need your attention float to the top."
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 10,
              marginBottom: 16,
            }}
          >
            <StatCard
              label="Total"
              value={connections.length}
              sub="All connections"
              accent="#2563eb"
            />
            <StatCard
              label="Active"
              value={activeConns.length}
              sub="Accepted connections"
              accent="#15803d"
            />
            <StatCard
              label="Pending"
              value={pendingConns.length}
              sub="Awaiting response"
              accent="#d97706"
            />
            <StatCard
              label="Rejected"
              value={
                connections.filter((c) =>
                  ["REJECTED", "REQUEST_REJECTED"].includes(
                    c.moduleAggrementStatus,
                  ),
                ).length
              }
              sub="Declined connections"
              accent="#dc2626"
            />
          </div>
          {loadConn ? (
            <Loading />
          ) : connections.length === 0 ? (
            <EmptyCard
              icon="🤝"
              heading="No connections yet"
              note="Use the finder above to invite companies."
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 12,
              }}
            >
              {sortedConnections.map((c) => {
                const {
                  canDecide,
                  isConn,
                  isDead,
                  isSettled,
                  canResend,
                  isDraft,
                  canSend,
                  isWaiting,
                  hasPdf,
                } = getConnFlags(c);

                return (
                  <RequestCard
                    key={c.moduleHistoryId}
                    name={c.connectionCompanyName}
                    date={c.sentOn}
                    module={c.moduleName}
                    status={c.senderDocumentStatus || c.moduleAggrementStatus}
                    highlight={canDecide || canResend || canSend}
                    tcFlow={
                      isConn
                        ? {
                            sender: c.senderDocumentStatus,
                            receiver: c.receiverDocumentStatus,
                          }
                        : undefined
                    }
                    info={
                      canResend
                        ? "Your T&C was rejected. Please resend updated terms."
                        : canDecide
                          ? "This company invited you — accept or decline to proceed."
                          : canSend
                            ? c.senderDocumentStatus === "RECEIVED_REJECTED"
                              ? "Your T&C was rejected by the consultancy. Send new terms."
                              : isDraft
                                ? "Continue your saved draft and send it."
                                : "You're connected! You can now send Terms & Conditions."
                            : isWaiting
                              ? "Waiting for the other party to review your T&C."
                              : undefined
                    }
                    actions={[
                      canDecide && (
                        <Btn
                          key="a"
                          v="success"
                          sm
                          onClick={() =>
                            setModal({
                              type: "decide",
                              conn: c,
                              action: "ACCEPTED",
                              onConfirm: () =>
                                decideConn(c.moduleHistoryId, "ACCEPTED"),
                            })
                          }
                        >
                          ✓ Accept
                        </Btn>
                      ),
                      canDecide && (
                        <Btn
                          key="r"
                          v="red"
                          sm
                          onClick={() =>
                            setModal({
                              type: "decide",
                              conn: c,
                              action: "REJECTED",
                              onConfirm: () =>
                                decideConn(c.moduleHistoryId, "REJECTED"),
                            })
                          }
                        >
                          ✕ Decline
                        </Btn>
                      ),
                      canResend && (
                        <Btn
                          key="rs"
                          v="amber"
                          sm
                          onClick={() =>
                            setModal({
                              type: "tc-send",
                              item: c,
                              isResend: true,
                            })
                          }
                        >
                          ↻ Resend T&C
                        </Btn>
                      ),
                      canSend && (
                        <Btn
                          key="tc"
                          v="primary"
                          sm
                          onClick={() => setModal({ type: "tc-send", item: c })}
                        >
                          📄{" "}
                          {isDraft
                            ? "Continue draft"
                            : c.receiverTerms
                              ? "Update T&C"
                              : "Send T&C"}
                        </Btn>
                      ),

                      isWaiting && (
                        <span
                          key="w"
                          style={{ fontSize: 12, color: "#6b7280" }}
                        >
                          ⏳ Awaiting their review…
                        </span>
                      ),
                      isSettled && (
                        <span
                          key="ok"
                          style={{
                            fontSize: 12,
                            color: "#15803d",
                            fontWeight: 600,
                          }}
                        >
                          ✓ T&C fully accepted
                        </span>
                      ),
                      isDead && (
                        <span
                          key="ded"
                          style={{ fontSize: 12, color: "#9ca3af" }}
                        >
                          Connection declined
                        </span>
                      ),
                      hasPdf && (
                        <Btn
                          key="pdf"
                          v="ghost"
                          sm
                          onClick={() =>
                            setModal({
                              type: "pdf-view",
                              moduleHistoryId: c.moduleHistoryId,
                              companyName: c.connectionCompanyName,
                            })
                          }
                        >
                          👁 View T&C
                        </Btn>
                      ),
                    ].filter(Boolean)}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* SECTION 4 — REVIEW RECEIVED T&C */}
        <section style={{ animation: "cn-fade-up .55s ease both" }}>
          <SectionHeader
            icon="📬"
            title="Review Received Terms & Conditions"
            count={
              tcToReview.length > 0
                ? `${tcToReview.length} need review`
                : tcReceived.length
            }
            accent="#dc2626"
            info="Your partners have sent you their Terms & Conditions for review. Read them carefully, then accept, reject, or propose counter-terms."
          />
          {loadTCR ? (
            <Loading />
          ) : tcReceived.length === 0 ? (
            <EmptyCard
              icon="📬"
              heading="No T&C received yet"
              note="When a partner sends you their T&C, it'll appear here."
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 12,
              }}
            >
              {tcReceived.map((item) => {
                const isAccepted =
                  item.receiverDocumentStatus === "RECEIVED_ACCEPTED";
                const isRejected =
                  item.receiverDocumentStatus === "RECEIVED_REJECTED";
                const canR = !isAccepted && !isRejected;
                // Find the matching connection so we can open tc-send with full data
                const matchedConn =
                  connections.find(
                    (c) => c.moduleHistoryId === item.moduleHistoryId,
                  ) || item;
                return (
                  <RequestCard
                    key={item.moduleHistoryId}
                    name={item.companyName}
                    module={item.moduleName}
                    status={
                      item.receiverDocumentStatus || item.moduleAggrementStatus
                    }
                    highlight={canR || isRejected}
                    info={
                      canR
                        ? "Review their Terms & Conditions and respond. You can accept, reject, or propose counter-terms."
                        : isRejected
                          ? "You rejected their T&C. You can now send your own terms."
                          : undefined
                    }
                    actions={[
                      canR && (
                        <Btn
                          key="r"
                          v="primary"
                          sm
                          onClick={() => setModal({ type: "review-tc", item })}
                        >
                          📋 Review & Respond
                        </Btn>
                      ),
                      isAccepted && (
                        <span
                          key="ok"
                          style={{
                            fontSize: 12,
                            color: "#15803d",
                            fontWeight: 600,
                          }}
                        >
                          ✓ You accepted these T&C
                        </span>
                      ),
                      isRejected && (
                        <Btn
                          key="send"
                          v="primary"
                          sm
                          onClick={() =>
                            setModal({ type: "tc-send", item: matchedConn })
                          }
                        >
                          📄 Send T&C
                        </Btn>
                      ),
                      isRejected && (
                        <Btn
                          key="tmpl"
                          v="outline"
                          sm
                          onClick={() =>
                            setModal({
                              type: "recruitment-agreement",
                              item: matchedConn,
                            })
                          }
                        >
                          📋 Use Template
                        </Btn>
                      ),
                      item.moduleHistoryId && (
                        <Btn
                          key="pdf"
                          v="ghost"
                          sm
                          onClick={() =>
                            setModal({
                              type: "pdf-view",
                              moduleHistoryId: item.moduleHistoryId,
                              companyName: item.connectionCompanyName,
                            })
                          }
                        >
                          👁 View T&C
                        </Btn>
                      ),
                    ].filter(Boolean)}
                  />
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* ═══ MODALS ═══ */}
      {modal?.type === "decide" && (
        <DecideModal
          conn={modal.conn}
          action={modal.action}
          onConfirm={modal.onConfirm}
          onClose={() => setModal(null)}
        />
      )}
      {(modal?.type === "tc-send" || modal?.type === "tc-send-pending") && (
        <TCModal
          title={
            modal.isResend
              ? "Resend T&C"
              : modal.item?.receiverTerms
                ? "Update T&C"
                : "Send T&C"
          }
          sub={`${modal.item?.connectionCompanyName || modal.item?.companyName || ""} · ${modal.item?.moduleName || "—"}`}
          initText={modal.item?.receiverTerms || modal.item?.senderTerms || ""}
          refHTML={modal.item?.senderTerms || ""}
          refLabel={
            modal.isResend ? "Your previous T&C" : "Their T&C (reference)"
          }
          isResend={modal.isResend}
          showDraft={!modal.isResend}
          showAgreementBuilder={true}
          onOpenBuilder={() =>
            setModal({
              type: "recruitment-agreement",
              item: modal.item,
              isResend: modal.isResend,
            })
          }
          onSubmit={(t, r, d) =>
            modal.isResend
              ? resendTC(modal.item.moduleHistoryId, t, r)
              : sendTC(modal.item.moduleHistoryId, t, r, d)
          }
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "review-tc" && (
        <ReviewTCModal
          tc={modal.item}
          onAccept={(r) => acceptTC(modal.item.moduleHistoryId, r)}
          onReject={(r) => rejectTC(modal.item.moduleHistoryId, r)}
          onCounter={(t, r) => sendTC(modal.item.moduleHistoryId, t, r, false)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "recruitment-agreement" && (
        <RecruitmentAgreementModal
          moduleHistoryId={modal.item?.moduleHistoryId}
          connectionId={modal.item?.connectionId}
          isResend={modal.isResend ?? false}
          onSave={() => {
            loadConnections();
          }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "pdf-view" && (
        <PDFViewerModal
          moduleHistoryId={modal.moduleHistoryId}
          companyName={modal.companyName || modal.connectionCompanyName}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   CONSULTANT VIEW
═══════════════════════════════════════════ */
function ConsultantView() {
  const { isConsultantAdmin } = useWorkspace();
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState("");

  // ── Find Companies (new) ──
  const [consMods, setConsMods] = useState([]);
  const [selConsMod, setSelConsMod] = useState(null);
  const [consTenants, setConsTenants] = useState([]);
  const [loadConsMods, setLoadConsMods] = useState(true);
  const [loadConsTens, setLoadConsTens] = useState(false);
  const [consInviting, setConsInviting] = useState(null);
  const [consSentIds, setConsSentIds] = useState(new Set());
  const [findSearch, setFindSearch] = useState("");

  const [allConns, setAllConns] = useState([]);
  const [loadAll, setLoadAll] = useState(true);
  const [invites, setInvites] = useState([]);
  const [loadInv, setLoadInv] = useState(true);
  const [tcRecv, setTcRecv] = useState([]);
  const [loadTCR, setLoadTCR] = useState(false);

  const loadAll_ = useCallback(() => {
    setLoadAll(true);
    axios
      .get(`${CADMIN}/modules-history/all`)
      .then((r) => setAllConns(normDeep(r.data.data || [])))
      .catch(console.error)
      .finally(() => setLoadAll(false));
  }, []);
  const loadInv_ = useCallback(() => {
    setLoadInv(true);
    axios
      .get(`${CADMIN}/modules/invited`)
      .then((r) => setInvites(normDeep(r.data.data || [])))
      .catch(console.error)
      .finally(() => setLoadInv(false));
  }, []);
  const loadTCR_ = useCallback(() => {
    setLoadTCR(true);
    axios
      .get(`${CADMIN}/terms_and_condition/received`)
      .then((r) => setTcRecv(normDeep(r.data.data || [])))
      .catch(console.error)
      .finally(() => setLoadTCR(false));
  }, []);

  useEffect(() => {
    // Load consultancy modules for the "Find Companies" section
    setLoadConsMods(true);
    axios
      .get(`${CADMIN}/modules`)
      .then((r) => {
        const l = r.data.data || [];
        setConsMods(l);
        if (l.length) {
          const def =
            l.find((m) => m.connectionModuleName !== "Consultancy") || l[0];
          setSelConsMod(def);
        }
      })
      .catch(() => toast.error("Failed to load modules."))
      .finally(() => setLoadConsMods(false));

    loadAll_();
    loadInv_();
    loadTCR_();
  }, []); // eslint-disable-line

  // Load tenants when selected module changes
  useEffect(() => {
    if (!selConsMod) return;
    setLoadConsTens(true);
    setConsTenants([]);
    setConsSentIds(new Set());
    axios
      .get(`${CADMIN}/${selConsMod.moduleId}/tenants`)
      .then((r) => setConsTenants(normDeep(r.data.data || [])))
      .catch(() => toast.error("Failed to load companies."))
      .finally(() => setLoadConsTens(false));
  }, [selConsMod]); // eslint-disable-line

  const consInvite = async (t) => {
    setConsInviting(t.tenantRegId);
    try {
      await axios.post(CADMIN, {
        tenantRegId: t.tenantRegId,
        moduleInviteRequestDTOList: [
          { moduleName: normModule(selConsMod.connectionModuleName) },
        ],
      });
      toast.success(`Invite sent to ${t.companyName}!`);
      setConsSentIds((p) => new Set([...p, t.tenantRegId]));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    } finally {
      setConsInviting(null);
    }
  };

  const pendingInvites = invites.filter(
    (i) => !["ACCEPTED", "REJECTED"].includes(i.moduleAgreementStatus),
  );

  const allRows = allConns.flatMap((conn) =>
    (conn.connectionModuleHistoryAssignedEmployeeResponseDTOS || [])
      .filter((mod) => mod.moduleName === "Recruitment")
      .map((mod) => ({ conn, mod })),
  );

  const decide = async (mhId, decision) => {
    try {
      await axios.put(`${CADMIN}/module/accept/reject`, {
        moduleHistoryId: mhId,
        decision,
      });
      toast.success(`Invite ${decision.toLowerCase()}!`);
      loadInv_();
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    }
  };

  const sendTC = async (mhId, t, r, d) => {
    try {
      const pdfBlob = await generatePDFBlob(t);
      await submitAgreement({
        moduleHistoryId: mhId,
        pdfBlob,
        senderDocumentStatus: d ? "DRAFT" : "SENT",
        isDraft: !!d,
        hrBase: CADMIN,
      });
      toast.success(d ? "Draft saved!" : "T&C sent!");
      loadAll_();
      loadTCR_();
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    }
  };
  const resendTC = async (mhId, t, r) => {
    try {
      const pdfBlob = await generatePDFBlob(t);
      await submitAgreement({
        moduleHistoryId: mhId,
        pdfBlob,
        senderDocumentStatus: "RESENT",
        isDraft: false,
        hrBase: CADMIN,
      });
      toast.success("T&C resent!");
      loadAll_();
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    }
  };
  const acceptTC = async (mhId, reason) => {
    try {
      await axios.put(`${CADMIN}/receiver/terms-decision`, {
        moduleHistoryId: mhId,
        receiverDocumentStatus: "RECEIVED_ACCEPTED",
        ...(reason ? { reason } : {}),
      });
      toast.success("Terms accepted!");
      loadTCR_();
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    }
  };
  const rejectTC = async (mhId, reason) => {
    try {
      await axios.put(`${CADMIN}/receiver/terms-decision`, {
        moduleHistoryId: mhId,
        receiverDocumentStatus: "RECEIVED_REJECTED",
        ...(reason ? { reason } : {}),
      });
      toast.success("Terms rejected.");
      loadTCR_();
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed.");
    }
  };

  const getConsConnFlags = (mod) => {
    const connStatus = mod.moduleAggrementStatus || "";
    const isConn = ["ACCEPTED", "REQUEST_ACCEPTED"].includes(connStatus);
    const isDead = ["REJECTED", "REQUEST_REJECTED"].includes(connStatus);
    const sds = mod.senderStatus || "";
    const isSettled = sds === "SENT_ACCEPTED";
    const canResend = !isSettled && sds === "SENT_REJECTED";
    const isDraft = sds === "DRAFT";
    const canSend =
      isConn &&
      !isSettled &&
      !canResend &&
      (!sds || isDraft || sds === "RECEIVED_REJECTED");
    const isWaiting =
      isConn &&
      !isSettled &&
      !canResend &&
      !canSend &&
      (sds === "SENT" || sds === "RESENT");
    const hasPdf = !!mod.moduleHistoryId && !!sds;
    return {
      isConn,
      isDead,
      isSettled,
      canResend,
      isDraft,
      canSend,
      isWaiting,
      hasPdf,
      sds,
    };
  };

  const consConnRows = allRows.filter(({ mod }) => {
    const { isConn } = getConsConnFlags(mod);
    return isConn;
  });

  const tcToSend = consConnRows.filter(({ mod }) => {
    const { canSend, canResend } = getConsConnFlags(mod);
    return canSend || canResend;
  });
  const tcToReview = tcRecv.filter(
    (r) => r.termsAndConditionStatus !== "RECEIVED_ACCEPTED",
  );
  const totalActions =
    pendingInvites.length + tcToSend.length + tcToReview.length;

  const share = () =>
    navigator.clipboard
      .writeText("https://www.ourdept.com/modules")
      .then(() => toast.success("Link copied!"))
      .catch(() => toast.error("Failed to copy."));

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f9fafb",
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      <style>{`
        @keyframes cn-spin { to { transform: rotate(360deg); } }
        @keyframes cn-modal-in { from { opacity:0; transform:scale(0.96) translateY(8px); } to { opacity:1; transform:none; } }
        @keyframes cn-fade-up { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }
        * { box-sizing: border-box; }
      `}</style>

      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #e5e7eb",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 100,
          gap: 16,
        }}
      >
        {/* Left — title */}
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#9ca3af",
              letterSpacing: "0.06em",
            }}
          >
            Company {/* or Consultancy */}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: "#111827",
                margin: 0,
                letterSpacing: "-0.4px",
              }}
            >
              Connections
            </h1>
            {totalActions > 0 && (
              <span
                style={{
                  background: "#dc2626",
                  color: "#fff",
                  borderRadius: 12,
                  padding: "2px 9px",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {totalActions} need action
              </span>
            )}
          </div>
        </div>

        {/* Right — patriotic banner, horizontal */}
        <div
          style={{
            flex: 1,
            background:
              "linear-gradient(135deg,#fff7ed 0%,#fff 50%,#f0fdf4 100%)",
            border: "1.5px solid #ff9933",
            borderRadius: 10,
            padding: "7px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 2px 8px rgba(255,153,51,0.15)",
          }}
        >
          {/* Tricolor left stripe */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: 3,
              background:
                "linear-gradient(180deg,#ff9933 33%,#fff 33%,#fff 66%,#138808 66%)",
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              paddingLeft: 8,
            }}
          >
            <span style={{ fontSize: 20, flexShrink: 0 }}>🇮🇳</span>
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: "#111827",
                  lineHeight: 1.2,
                }}
              >
                Help India's Businesses Grow Stronger
              </div>
              <div style={{ fontSize: 11, color: "#4b5563", lineHeight: 1.4 }}>
                Our <strong>Consultancy Module is 100% free</strong> —
                connecting Indian businesses with the best recruitment partners.
              </div>
            </div>
          </div>

          <button
            onClick={share}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              border: "1.5px solid #ff9933",
              background: "#fff7ed",
              color: "#92400e",
              cursor: "pointer",
              fontFamily: "inherit",
              whiteSpace: "nowrap",
              flexShrink: 0,
              transition: "all .15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                "0 3px 10px rgba(255,153,51,0.35)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "";
              e.currentTarget.style.transform = "";
            }}
          >
            🇮🇳 Share Free · No credit card
          </button>
        </div>
      </div>

      {/* Info bar */}
      <div
        style={{
          background: "#f0fdf4",
          borderBottom: "1px solid #bbf7d0",
          padding: "10px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ fontSize: 20 }}>ℹ️</span>
        <span style={{ fontSize: 13, color: "#15803d" }}>
          This is your <strong>Consultancy Connections hub</strong> — see all
          client connections, respond to invites, and manage Terms & Conditions
          with partner companies, all in one scrollable page.
        </span>
      </div>

      <div
        style={{
          padding: "10px",
          display: "flex",
          flexDirection: "column",
          gap: 32,
          margin: "0 auto",
        }}
      >
        {/* FIND COMPANIES */}
        <section style={{ animation: "cn-fade-up .3s ease both" }}>
          <SectionHeader
            icon="🔍"
            title="Find Companies to Connect With"
            accent="#2563eb"
            info="Browse companies and send them a connection invite. They'll get notified and can accept or decline."
          />

          <div className="cone-stats-row">
            {[
              {
                label: "Available companies",
                sub: "Total companies available",
                value: consTenants.length,
                tone: "blue",
              },
              {
                label: "Already connected",
                sub: "Accepted connections",
                value: consTenants.filter((t) =>
                  ["REQUEST_ACCEPTED", "ACCEPTED"].includes(
                    t.connectionModuleAggrementStatus,
                  ),
                ).length,
                tone: "green",
              },
              {
                label: "Invites sent this session",
                sub: "Current session activity",
                value: consSentIds.size,
                tone: "violet",
              },
            ].map((stat) => (
              <article
                key={stat.label}
                className={`cand-stat cand-stat--${stat.tone}`}
              >
                <span className="cand-stat__bar" />
                <div className="cand-stat__body">
                  <div className="cand-stat__text">
                    <p className="cand-stat__label">{stat.label}</p>
                    <p className="cand-stat__sub">{stat.sub}</p>
                  </div>
                  <h3 className="cand-stat__value">{stat.value}</h3>
                </div>
              </article>
            ))}
          </div>

          {loadConsMods ? (
            <Loading text="Loading modules…" />
          ) : !consMods.length ? (
            <EmptyCard heading="No modules available" />
          ) : (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "14px 16px",
                  borderBottom: "1px solid #f3f4f6",
                  background: "#fafafa",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#9ca3af",
                    letterSpacing: "0.05em",
                    marginBottom: 8,
                  }}
                >
                  Select Module
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {consMods.map((m) => {
                    const on = selConsMod?.moduleId === m.moduleId;
                    const isComingSoon =
                      m.connectionModuleName === "Consultancy";
                    return (
                      <button
                        key={m.moduleId}
                        disabled={isComingSoon}
                        onClick={() => !isComingSoon && setSelConsMod(m)}
                        title={
                          isComingSoon
                            ? "Beta version — coming soon"
                            : undefined
                        }
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 14px",
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: isComingSoon ? "not-allowed" : "pointer",
                          border: `1.5px solid ${
                            isComingSoon
                              ? "#e5e7eb"
                              : on
                                ? "#2563eb"
                                : "#d1d5db"
                          }`,
                          background: isComingSoon
                            ? "#f3f4f6"
                            : on
                              ? "#2563eb"
                              : "#fff",
                          color: isComingSoon
                            ? "#9ca3af"
                            : on
                              ? "#fff"
                              : "#374151",
                          opacity: isComingSoon ? 0.7 : 1,
                          fontFamily: "inherit",
                        }}
                      >
                        {m.connectionModuleName}
                        {isComingSoon && (
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              padding: "1px 6px",
                              borderRadius: 8,
                              background: "#e5e7eb",
                              color: "#6b7280",
                            }}
                          >
                            BETA · COMING SOON
                          </span>
                        )}
                        {!isComingSoon && on && " ✓"}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <input
                  value={findSearch}
                  onChange={(e) => setFindSearch(e.target.value)}
                  placeholder="🔍  Search by company name or domain…"
                  style={{
                    width: "100%",
                    padding: "9px 14px",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    fontSize: 13,
                    outline: "none",
                    background: "#f9fafb",
                  }}
                />
              </div>
              <div>
                {loadConsTens ? (
                  <Loading text="Loading companies…" />
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(280px, 1fr))",
                      gap: 12,
                      padding: 12,
                    }}
                  >
                    {consTenants
                      .filter((t) => {
                        const q = findSearch.toLowerCase();
                        return (
                          !q ||
                          (t.companyName || "").toLowerCase().includes(q) ||
                          (t.subDomain || "").toLowerCase().includes(q)
                        );
                      })
                      .map((t) => (
                        <CompanyInviteCard
                          key={t.tenantRegId}
                          tenant={t}
                          selMod={selConsMod}
                          onInvite={consInvite}
                          inviting={consInviting}
                          sent={consSentIds.has(t.tenantRegId)}
                        />
                      ))}
                    {consTenants.filter((t) => {
                      const q = findSearch.toLowerCase();
                      return (
                        !q ||
                        (t.companyName || "").toLowerCase().includes(q) ||
                        (t.subDomain || "").toLowerCase().includes(q)
                      );
                    }).length === 0 && (
                      <EmptyCard heading="No companies found" icon="🏢" />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* ALL CONNECTIONS */}
        <section style={{ animation: "cn-fade-up .35s ease both" }}>
          <SectionHeader
            icon="🌐"
            title="All Client Connections"
            count={allRows.length}
            accent="#2563eb"
            info="Every connection your consultancy has with client companies, broken down by module. Admins can assign an employee to manage each connection."
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 10,
              marginBottom: 16,
            }}
          >
            <StatCard
              label="Total modules"
              value={allRows.length}
              sub="Across all clients"
              accent="#2563eb"
            />
            <StatCard
              label="Active"
              value={
                allRows.filter(({ mod }) =>
                  ["ACCEPTED", "REQUEST_ACCEPTED"].includes(
                    mod.moduleAggrementStatus,
                  ),
                ).length
              }
              sub="Live connections"
              accent="#15803d"
            />
            <StatCard
              label="Unassigned"
              value={
                allRows.filter(({ mod }) => !mod.assignedEmployeeName).length
              }
              sub="Need assignment"
              accent="#d97706"
            />
            <StatCard
              label="Pending T&C"
              value={
                allRows.filter(
                  ({ mod }) =>
                    mod.senderStatus === "SENT" ||
                    mod.receiverStatus === "RE_RECEIVED",
                ).length
              }
              sub="Awaiting review"
              accent="#7c3aed"
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍  Search connections, modules, employees…"
              style={{
                width: "100%",
                padding: "9px 14px",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                fontSize: 13,
                outline: "none",
                background: "#fff",
              }}
            />
          </div>

          {loadAll ? (
            <Loading />
          ) : allRows.length === 0 ? (
            <EmptyCard
              icon="🌐"
              heading="No connections yet"
              note="Connections appear once companies invite your consultancy."
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 12,
              }}
            >
              {allRows
                .filter(({ conn, mod }) => {
                  const q = search.toLowerCase();
                  return (
                    !q ||
                    (conn.connectionCompanyName || "")
                      .toLowerCase()
                      .includes(q) ||
                    (mod.moduleName || "").toLowerCase().includes(q) ||
                    (mod.assignedEmployeeName || "").toLowerCase().includes(q)
                  );
                })
                .map(({ conn, mod }) => (
                  <div
                    key={mod.moduleHistoryId}
                    style={{
                      background: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: 14,
                      padding: 16,
                      boxShadow: "0 1px 3px rgba(0,0,0,.05)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 12,
                      }}
                    >
                      <Avatar name={conn.connectionCompanyName} sz={40} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 14,
                            color: "#111827",
                          }}
                        >
                          {conn.connectionCompanyName}
                        </div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>
                          {conn.connectionName}
                        </div>
                      </div>
                      <StatusPill status={mod.moduleAggrementStatus} />

                      {isConsultantAdmin &&
                        ["ACCEPTED", "REQUEST_ACCEPTED"].includes(
                          mod.moduleAggrementStatus,
                        ) && (
                          <Btn
                            v="neutral"
                            sm
                            onClick={() =>
                              setModal({
                                type: "assign",
                                ...mod,
                                connectionId: conn.connectionId,
                                connectionName: conn.connectionName,
                                fetchData: loadAll_,
                              })
                            }
                          >
                            {mod.assignedEmployeeName
                              ? "↺ Reassign"
                              : "+ Assign employee"}
                          </Btn>
                        )}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 8,
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          background: "#f9fafb",
                          borderRadius: 8,
                          padding: "8px 10px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            color: "#9ca3af",
                            fontWeight: 600,
                            marginBottom: 2,
                          }}
                        >
                          MODULE
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#374151",
                          }}
                        >
                          {mod.moduleName}
                        </div>
                      </div>
                      <div
                        style={{
                          background: "#f9fafb",
                          borderRadius: 8,
                          padding: "8px 10px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            color: "#9ca3af",
                            fontWeight: 600,
                            marginBottom: 2,
                          }}
                        >
                          ASSIGNED TO
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: mod.assignedEmployeeName
                              ? "#374151"
                              : "#d97706",
                          }}
                        >
                          {mod.assignedEmployeeName || "⚠ Unassigned"}
                        </div>
                      </div>
                    </div>
                    {(mod.senderStatus || mod.receiverStatus) && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ fontSize: 11, color: "#9ca3af" }}>
                          T&C:
                        </span>
                        {mod.senderStatus && (
                          <StatusPill status={mod.senderStatus} />
                        )}
                        {mod.senderStatus && mod.receiverStatus && (
                          <span style={{ fontSize: 10, color: "#d1d5db" }}>
                            →
                          </span>
                        )}
                        {mod.receiverStatus && (
                          <StatusPill status={mod.receiverStatus} />
                        )}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </section>

        {/* PENDING INVITES */}
        <section style={{ animation: "cn-fade-up .4s ease both" }}>
          <SectionHeader
            icon="📨"
            title="Invites from Companies"
            count={
              pendingInvites.length > 0
                ? `${pendingInvites.length} need response`
                : invites.length
            }
            accent="#d97706"
            info="Client companies have sent your consultancy connection invites. Accept to start working together, or decline."
          />
          {loadInv ? (
            <Loading />
          ) : invites.length === 0 ? (
            <EmptyCard
              icon="📭"
              heading="No invites yet"
              note="Company invites will appear here."
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 12,
              }}
            >
              {invites.map((i) => {
                const isPend = !["ACCEPTED", "REJECTED"].includes(
                  i.moduleAgreementStatus,
                );
                return (
                  <RequestCard
                    key={i.moduleHistoryId}
                    name={i.companyName}
                    module={i.moduleName}
                    date={i.sentOn}
                    status={i.termsAndConditionStatus}
                    highlight={isPend}
                    info={
                      isPend
                        ? "A client company wants to connect with your consultancy. Accept to begin the T&C exchange."
                        : undefined
                    }
                    actions={
                      isPend
                        ? [
                            <Btn
                              key="a"
                              v="success"
                              sm
                              onClick={() =>
                                setModal({
                                  type: "decide-cons",
                                  inv: i,
                                  action: "ACCEPTED",
                                })
                              }
                            >
                              ✓ Accept
                            </Btn>,
                            <Btn
                              key="r"
                              v="red"
                              sm
                              onClick={() =>
                                setModal({
                                  type: "decide-cons",
                                  inv: i,
                                  action: "REJECTED",
                                })
                              }
                            >
                              ✕ Decline
                            </Btn>,
                          ]
                        : [
                            <span
                              key="s"
                              style={{ fontSize: 12, color: "#9ca3af" }}
                            >
                              {i.moduleAgreementStatus === "REJECTED"
                                ? "You declined"
                                : "Accepted ✓"}
                            </span>,
                          ]
                    }
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* SEND T&C */}
        <section style={{ animation: "cn-fade-up .45s ease both" }}>
          <SectionHeader
            icon="📄"
            title="Send Terms & Conditions"
            count={
              tcToSend.length > 0
                ? `${tcToSend.length} ready to send`
                : consConnRows.length || undefined
            }
            accent="#7c3aed"
            info="After accepting a connection, send your T&C to the client. Use our standard Recruitment Agreement template or write your own."
          />
          {loadAll ? (
            <Loading />
          ) : consConnRows.length === 0 ? (
            <EmptyCard
              icon="📝"
              heading="No active connections yet"
              note="Accept a connection invite to start sending T&C."
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 12,
              }}
            >
              {consConnRows.map(({ conn, mod }) => {
                const {
                  isSettled,
                  canResend,
                  isDraft,
                  canSend,
                  isWaiting,
                  hasPdf,
                  sds,
                } = getConsConnFlags(mod);
                const item = {
                  ...mod,
                  connectionCompanyName: conn.connectionCompanyName,
                  connectionId: conn.connectionId,
                  moduleName: mod.moduleName,
                  senderTerms: mod.senderTerms || "",
                };
                return (
                  <RequestCard
                    key={mod.moduleHistoryId}
                    name={conn.connectionCompanyName}
                    date={mod.sentOn}
                    module={mod.moduleName}
                    status={sds || mod.moduleAggrementStatus}
                    highlight={!isSettled && (canResend || canSend)}
                    tcFlow={{ sender: sds || undefined }}
                    info={
                      canResend
                        ? "Your T&C was rejected. Revise and resend."
                        : canSend
                          ? isDraft
                            ? "Continue your saved draft and send it."
                            : "You're connected! You can now send Terms & Conditions."
                          : isWaiting
                            ? "Waiting for the client to review your T&C."
                            : undefined
                    }
                    actions={[
                      isSettled && (
                        <span
                          key="ok"
                          style={{
                            fontSize: 12,
                            color: "#15803d",
                            fontWeight: 600,
                          }}
                        >
                          ✓ T&C fully agreed
                        </span>
                      ),
                      !isSettled && canResend && (
                        <Btn
                          key="rs"
                          v="amber"
                          sm
                          onClick={() =>
                            setModal({
                              type: "cons-tc-send",
                              item,
                              isResend: true,
                            })
                          }
                        >
                          ↻ Resend T&C
                        </Btn>
                      ),
                      !isSettled && canResend && (
                        <Btn
                          key="rs-tmpl"
                          v="outline"
                          sm
                          onClick={() =>
                            setModal({
                              type: "recruitment-agreement-cons",
                              item,
                              isResend: true,
                              rejectionReason: mod.reason || null,
                              prefillDefaults: {
                                recruitmentFee: "8.33",
                                paymentDays: "35",
                                replacementDays: "90",
                                replacementDeadlineDays: "30",
                                noticePeriod: "30",
                                agreementValidity: "3 Years",
                                jurisdiction: "Bangalore, Karnataka",
                              },
                            })
                          }
                        >
                          📋 Use Template
                        </Btn>
                      ),
                      !isSettled && !canResend && isWaiting && (
                        <span
                          key="w"
                          style={{ fontSize: 12, color: "#6b7280" }}
                        >
                          ⏳ Awaiting their review…
                        </span>
                      ),
                      !isSettled && !canResend && canSend && (
                        <Btn
                          key="s"
                          v="primary"
                          sm
                          onClick={() =>
                            setModal({ type: "cons-tc-send", item })
                          }
                        >
                          📄 {isDraft ? "Continue draft" : "Send T&C"}
                        </Btn>
                      ),
                      !isSettled && !canResend && canSend && (
                        <Btn
                          key="tmpl"
                          v="outline"
                          sm
                          onClick={() =>
                            setModal({
                              type: "recruitment-agreement-cons",
                              item,
                            })
                          }
                        >
                          📋 Use Template
                        </Btn>
                      ),
                      hasPdf && (
                        <Btn
                          key="pdf"
                          v="ghost"
                          sm
                          onClick={() =>
                            setModal({
                              type: "pdf-view",
                              moduleHistoryId: mod.moduleHistoryId,
                              companyName: conn.connectionCompanyName,
                            })
                          }
                        >
                          👁 View T&C
                        </Btn>
                      ),
                    ].filter(Boolean)}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* RECEIVED T&C */}
        <section style={{ animation: "cn-fade-up .5s ease both" }}>
          <SectionHeader
            icon="📬"
            title="Received Terms & Conditions"
            count={
              tcToReview.length > 0
                ? `${tcToReview.length} need review`
                : tcRecv.length
            }
            accent="#dc2626"
            info="Clients have sent you their Terms & Conditions. Review them carefully and accept, reject, or propose counter-terms."
          />
          {loadTCR ? (
            <Loading />
          ) : tcRecv.length === 0 ? (
            <EmptyCard
              icon="📬"
              heading="No T&C received"
              note="When a client sends you their T&C, it'll appear here."
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 12,
              }}
            >
              {tcRecv.map((item) => {
                const status =
                  item.termsAndConditionStatus ??
                  item.receiverStatus ??
                  item.senderStatus;
                const canR =
                  status !== "RECEIVED_ACCEPTED" &&
                  status !== "RECEIVED_REJECTED";
                const rejectedByMe = status === "RECEIVED_REJECTED";
                const refBody =
                  item.receiverTerms ||
                  item.documentText ||
                  item.senderTerms ||
                  "";
                return (
                  <RequestCard
                    key={item.moduleHistoryId}
                    name={item.companyName}
                    module={item.moduleName}
                    status={item.termsAndConditionStatus}
                    highlight={canR || rejectedByMe}
                    info={
                      canR
                        ? "Review the T&C from this client and respond."
                        : rejectedByMe
                          ? "You rejected their T&C. You can send your own terms instead."
                          : undefined
                    }
                    actions={[
                      canR ? (
                        <Btn
                          key="r"
                          v="primary"
                          sm
                          onClick={() =>
                            setModal({ type: "cons-accept-tc", item })
                          }
                        >
                          📋 Review & Respond
                        </Btn>
                      ) : status === "RECEIVED_ACCEPTED" ? (
                        <span
                          key="ok"
                          style={{
                            fontSize: 12,
                            color: "#15803d",
                            fontWeight: 600,
                          }}
                        >
                          ✓ You accepted
                        </span>
                      ) : null,
                      rejectedByMe && (
                        <Btn
                          key="own"
                          v="primary"
                          sm
                          onClick={() =>
                            setModal({
                              type: "cons-tc-send",
                              item: {
                                ...item,
                                senderTerms: "",
                                connectionId: item.connectionId,
                              },
                              refHTML: refBody,
                              refLabel: "Their T&C (rejected by you)",
                            })
                          }
                        >
                          📄 Send Your T&C
                        </Btn>
                      ),
                      rejectedByMe && (
                        <Btn
                          key="tmpl"
                          v="outline"
                          sm
                          onClick={() =>
                            setModal({
                              type: "recruitment-agreement-cons",
                              item,
                              isResend: true, // ← triggers PDF prefill
                              rejectionReason: item.reason || null, // ← shows rejection banner
                              prefillDefaults: {
                                recruitmentFee: "8.33",
                                paymentDays: "35",
                                replacementDays: "90",
                                replacementDeadlineDays: "30",
                                noticePeriod: "30",
                                agreementValidity: "3 Years",
                                jurisdiction: "Bangalore, Karnataka",
                              },
                            })
                          }
                        >
                          📋 Use Template
                        </Btn>
                      ),
                      item.moduleHistoryId && (
                        <Btn
                          key="pdf"
                          v="ghost"
                          sm
                          onClick={() =>
                            setModal({
                              type: "pdf-view",
                              moduleHistoryId: item.moduleHistoryId,
                              companyName: item.connectionCompanyName,
                            })
                          }
                        >
                          👁 View T&C
                        </Btn>
                      ),
                    ].filter(Boolean)}
                  />
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* MODALS */}
      {modal?.type === "assign" && (
        <AssignModal {...modal} onClose={() => setModal(null)} />
      )}
      {modal?.type === "decide-cons" && (
        <DecideModal
          conn={modal.inv}
          action={modal.action}
          onConfirm={() => decide(modal.inv.moduleHistoryId, modal.action)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "cons-tc-send" && (
        <TCModal
          title={
            modal.isResend
              ? "Resend T&C"
              : modal.item?.senderTerms
                ? "Continue T&C"
                : "Send T&C"
          }
          sub={`#${modal.item?.connectionId} · ${modal.item?.moduleName}`}
          initText={modal.item?.senderTerms || ""}
          refHTML={
            modal.refHTML ?? (modal.isResend ? modal.item?.senderTerms : "")
          }
          refLabel={modal.refLabel ?? "Your previous T&C"}
          isResend={modal.isResend}
          showDraft={!modal.isResend}
          showAgreementBuilder={true}
          onOpenBuilder={() =>
            setModal({
              type: "recruitment-agreement-cons",
              item: modal.item,
              isResend: modal.isResend,
            })
          }
          onSubmit={(t, r, d) =>
            modal.isResend
              ? resendTC(modal.item.moduleHistoryId, t, r)
              : sendTC(modal.item.moduleHistoryId, t, r, d)
          }
          onClose={() => setModal(null)}
        />
      )}
      {(modal?.type === "recruitment-agreement" ||
        modal?.type === "recruitment-agreement-cons") && (
        <RecruitmentAgreementModal
          moduleHistoryId={modal.item?.moduleHistoryId}
          connectionId={modal.item?.connectionId}
          isResend={modal.isResend ?? false}
          rejectionReason={modal.rejectionReason ?? null}
          prefillItem={modal.item ?? null}
          prefillDefaults={modal.prefillDefaults ?? null}
          onSave={() => {}}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "cons-review-resp" && (
        <AcceptModal
          item={{
            ...modal.item,
            receiverTerms: modal.item.receiverTerms || modal.item.documentText,
          }}
          onAccept={(r) => acceptTC(modal.item.moduleHistoryId, r)}
          onReject={(r) => rejectTC(modal.item.moduleHistoryId, r)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "cons-accept-tc" && (
        <AcceptModal
          item={modal.item}
          onAccept={(r) => acceptTC(modal.item.moduleHistoryId, r)}
          onReject={(r) => rejectTC(modal.item.moduleHistoryId, r)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "pdf-view" && (
        <PDFViewerModal
          moduleHistoryId={modal.moduleHistoryId}
          companyName={modal.companyName}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
